import React from "react";
import {
  Calendar,
  Users,
  Globe,
  Sparkles,
  ArrowRight,
  MapPin,
  Zap,
  Shield,
  Heart,
  Target,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { HomepageNavbar } from "../../components/HomepageNavbar";
import { HomepageFooter } from "../../components/HomepageFooter";
import { useNavigate } from "react-router-dom";
import "../../styles/globals.css";

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>
          About Aiserwave - Event Technology Company | Event Software Provider
        </title>
        <meta
          name="description"
          content="Aiserwave builds structured, intelligent, scalable tools for events. The team focuses on reliability, speed, and accuracy. The platform serves organisers and enterprises across different sectors."
        />
        <meta
          name="keywords"
          content="event technology company, event software provider, about Aiserwave, event management company"
        />
        <link rel="canonical" href="https://aiserwave.com/about" />
      </Helmet>
      <div className="min-h-screen bg-white">
        <HomepageNavbar />
        {/* Hero */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#f3f4ff] via-white to-white border-b border-gray-100">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#17265a]/5 text-[#17265a] text-xs font-semibold mb-4">
                <Sparkles className="w-4 h-4" />
                Our story
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                Events that feel less like logistics and more like experiences.
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl leading-relaxed">
                Aiserwave exists to make it effortless for people to find,
                attend, and remember the moments that move them—from
                neighborhood meetups to global conferences.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="grid gap-12 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)] items-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Built for organizers, loved by attendees.
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    We started Aiserwave after years of juggling clunky ticket
                    links, lost QR codes, and chaotic check‑ins. Our goal is
                    simple: give organizers tools that feel modern and give
                    attendees a single, clean place to manage the experiences
                    they care about.
                  </p>
                  <p>
                    From discovery to day‑of logistics, Aiserwave is designed to
                    stay out of the way so you can focus on the people in the
                    room—not the platform running behind it.
                  </p>
                  <p>
                    Today, we power events across 150+ countries, helping
                    thousands of organizers create seamless experiences for
                    millions of attendees. Whether you&apos;re hosting a local
                    workshop or a global summit, we&apos;re here to make it
                    effortless.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="grid grid-cols-2 gap-4"
              >
                {[
                  {
                    icon: Calendar,
                    label: "Events hosted",
                    value: "10K+",
                  },
                  {
                    icon: Users,
                    label: "Attendees reached",
                    value: "50K+",
                  },
                  {
                    icon: Globe,
                    label: "Countries",
                    value: "150+",
                  },
                  {
                    icon: MapPin,
                    label: "Cities & communities",
                    value: "500+",
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#17265a]/10 text-[#17265a]">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {item.value}
                      </div>
                      <div className="text-xs font-medium text-gray-500">
                        {item.label}
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What we care about
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                These principles guide every decision we make—from product
                design to customer support.
              </p>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-3">
              {[
                {
                  icon: Heart,
                  title: "Real community",
                  body: "Events are excuses for people to meet, learn, and build together. We prioritize tools that amplify connection over vanity metrics.",
                },
                {
                  icon: Zap,
                  title: "Delightfully simple",
                  body: "From creating events to scanning tickets, every flow is designed to feel obvious—even if you're running your first event.",
                },
                {
                  icon: Globe,
                  title: "Global by default",
                  body: "From hybrid experiences to fully remote conferences, Aiserwave works wherever your attendees are in the world.",
                },
              ].map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="rounded-2xl border border-gray-200 bg-white p-6 flex flex-col gap-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#17265a]/10 text-[#17265a]">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {value.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {value.body}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Timeline / Journey */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our journey
              </h2>
              <p className="text-gray-600 text-lg">
                From a simple idea to powering events worldwide.
              </p>
            </motion.div>

            <div className="space-y-8">
              {[
                {
                  year: "2020",
                  title: "The beginning",
                  description:
                    "Started after experiencing the frustration of managing events across multiple platforms. We knew there had to be a better way.",
                },
                {
                  year: "2021",
                  title: "First 1,000 events",
                  description:
                    "Launched publicly and quickly gained traction with community organizers who loved our simple, focused approach.",
                },
                {
                  year: "2022",
                  title: "Global expansion",
                  description:
                    "Expanded to support events in 50+ countries, adding multi-currency support and localization features.",
                },
                {
                  year: "2023",
                  title: "10K milestone",
                  description:
                    "Reached 10,000 events hosted and introduced advanced analytics, custom branding, and API access for enterprise customers.",
                },
                {
                  year: "2024",
                  title: "Today",
                  description:
                    "Continuing to build tools that make event management effortless, with a focus on AI-powered recommendations and seamless integrations.",
                },
              ].map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-6"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-[#17265a] flex items-center justify-center text-white font-bold text-sm">
                      {milestone.year.slice(-2)}
                    </div>
                    {index < 4 && (
                      <div className="w-0.5 h-full bg-gray-200 mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What makes us different */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What makes us different
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                We&apos;re not just another event platform. Here&apos;s what
                sets us apart.
              </p>
            </motion.div>

            <div className="grid gap-6 md:grid-cols-2">
              {[
                {
                  icon: Target,
                  title: "Focused on what matters",
                  description:
                    "We don't try to be everything to everyone. We focus on making event discovery and attendance seamless, period.",
                },
                {
                  icon: Shield,
                  title: "Privacy-first",
                  description:
                    "Your data belongs to you. We never sell attendee information and give organizers full control over their data.",
                },
                {
                  icon: TrendingUp,
                  title: "Built to scale",
                  description:
                    "Whether you're hosting 10 people or 10,000, Aiserwave handles it with the same reliability and performance.",
                },
                {
                  icon: Award,
                  title: "Award-winning support",
                  description:
                    "Our team is here when you need us. We're known for fast response times and actually helpful support.",
                },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="rounded-xl border border-gray-200 bg-white p-6 flex gap-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#17265a]/10 text-[#17265a] flex-shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Ready to get started?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Whether you&apos;re organizing your first event or managing a
                global conference series, we&apos;re here to help.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/")}
                  className="bg-[#17265a] text-white px-8 py-4 rounded-lg hover:bg-[#0f1a3f] transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl font-semibold text-lg group"
                >
                  <span>Explore events</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-transparent border-2 border-[#17265a] text-[#17265a] px-8 py-4 rounded-lg hover:bg-[#17265a]/5 transition-all duration-300 flex items-center gap-2 font-semibold text-lg"
                >
                  <span>Talk to our team</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        <HomepageFooter />
      </div>
    </>
  );
};

export default AboutPage;
